/**
 * Утилиты для определения языка пользователя
 */

/**
 * Определяет язык для неавторизованных пользователей на основе настроек браузера
 * @returns 'ru' для русских и близких языков, 'en' для остальных
 */
export function detectLanguageForGuest(): 'ru' | 'en' {
  const browserLang = navigator.language.toLowerCase();
  
  // СНГ языки → русский
  const russianLike = ['ru', 'uk', 'be', 'kk'];
  if (russianLike.some(lang => browserLang.startsWith(lang))) {
    return 'ru';
  }
  
  // Все остальные → английский
  return 'en';
}

/**
 * Определяет язык для инициализации приложения
 * @param userLanguage - язык из БД (может быть null)
 * @param isAuthenticated - авторизован ли пользователь
 * @returns приоритетный язык для использования
 */
export function initializeLanguage(userLanguage: string | null, isAuthenticated: boolean): 'ru' | 'en' {
  const storedLanguage = getLanguageFromStorage();
  
  if (isAuthenticated) {
    // Для авторизованных пользователей:
    // 1. БД (если есть)
    // 2. localStorage (если БД пустая)
    // 3. Браузер (если и БД и localStorage пустые)
    
    if (userLanguage && ['ru', 'en'].includes(userLanguage)) {
      // Синхронизируем localStorage с БД
      if (storedLanguage !== userLanguage) {
        saveLanguageToStorage(userLanguage as 'ru' | 'en');
      }
      return userLanguage as 'ru' | 'en';
    }
    
    if (storedLanguage) {
      return storedLanguage;
    }
    
    return detectLanguageForGuest();
  } else {
    // Для гостей:
    // 1. localStorage (если есть)
    // 2. Браузер (если localStorage пустой)
    
    if (storedLanguage) {
      return storedLanguage;
    }
    
    return detectLanguageForGuest();
  }
}

/**
 * Сохраняет язык в localStorage
 * @param language - язык для сохранения
 */
export function saveLanguageToStorage(language: 'ru' | 'en'): void {
  localStorage.setItem('forsbot_language', language);
}

/**
 * Получает язык из localStorage
 * @returns сохраненный язык или null
 */
export function getLanguageFromStorage(): 'ru' | 'en' | null {
  const stored = localStorage.getItem('forsbot_language');
  return (stored === 'ru' || stored === 'en') ? stored : null;
}

/**
 * Конвертирует язык в формат i18n
 * @param language - язык в формате 'ru' | 'en'
 * @returns язык в формате 'RUS' | 'ENG'
 */
export function convertToI18nFormat(language: 'ru' | 'en'): 'RUS' | 'ENG' {
  return language === 'ru' ? 'RUS' : 'ENG';
}

/**
 * Конвертирует язык из формата i18n
 * @param language - язык в формате 'RUS' | 'ENG'
 * @returns язык в формате 'ru' | 'en'
 */
export function convertFromI18nFormat(language: 'RUS' | 'ENG'): 'ru' | 'en' {
  return language === 'RUS' ? 'ru' : 'en';
}
